import React, { useEffect } from 'react';
import './PrivacyPolicy.scss';

function PrivacyPolicy() {
  useEffect(() => {
    document.title = 'Privacy Policy | Artifit';
  }, []);

  return (
    <main className="pp-page">
      <article className="pp-content">
        <h1 className="pp-title">PRIVACY POLICY</h1>

        <section>
          <h2>1. Introduction</h2>
          <p>
            This Privacy Policy explains how Artifit d.o.o. Beograd („<strong>Artifit</strong>“) collects, uses, stores, and otherwise processes personal data in connection with the use of its website, communication with Artifit, and the use of its products and services.
          </p>
          <p>This Policy applies to:</p>
          <ul>
            <li>visitors to the Artifit website;</li>
            <li>individuals who contact Artifit via email or other communication channels;</li>
            <li>representatives of customers, business partners, and potential clients;</li>
            <li>users of Artifit’s software products and services, including products based on artificial intelligence technologies such as Gravity4AI, ProCoach11, EnergyAI, Digital Office, and other existing or future solutions developed or provided by Artifit to its users.</li>
          </ul>
        </section>

        <section>
          <h2>2. Data Controller Information</h2>
          <p>The controller of personal data is:</p>
          <p>
            Company <strong>ARTIFIT D.O.O. BEOGRAD-NOVI BEOGRAD</strong>, company registration number: 21669679, TIN: 112415459, with its registered office at Uroša Martinovića Street No. 18, Belgrade – New Belgrade.
          </p>
          <p>
            Artifit has appointed a Data Protection Officer. For any questions, you may contact us at: <strong><a href="mailto:privacy@artifit.rs">privacy@artifit.rs</a></strong>
          </p>
        </section>

        <section>
          <h2>3. Categories of Data We Process</h2>
          <p>Depending on the manner in which the Artifit website or products are used, the following categories of data may be processed:</p>
          <ol className="pp-list-paren">
            <li><span className="pp-marker">1)</span>Data you provide to us: first and last name; company name; business position/title; business email address; telephone number; content of messages, requests, and inquiries you submit to us.</li>
            <li><span className="pp-marker">2)</span>User Account Data – In the event of using our products that require authentication, we may process: username; data necessary for login and account management; information about the organization to which the user belongs.</li>
            <li><span className="pp-marker">3)</span>Technical Data – When using the website or our products, certain technical data may be processed, such as: IP address; device and browser information; system and security logs; system operation, usage, and performance data.</li>
            <li><span className="pp-marker">4)</span>Data processed through our products, further described in Section 4 of this Privacy Policy.</li>
          </ol>
        </section>

        <section>
          <h2>4. Data Processed Through Our Products</h2>
          <p>Our products may enable the integration of various data sources managed by the user, including: databases; documents; images; technical documentation; business records; other structured and unstructured sources of information.</p>
          <p>The content made available to the system by users may also contain personal data.</p>
          <p>The type of data processed depends on the specific product, the manner of its configuration, and the data sources connected to the system by the user, while the method of processing varies depending on the specific product and implementation model.</p>
          <p>The user is responsible for ensuring that the data entered into the system have been collected and are processed in accordance with applicable personal data protection regulations.</p>
          <p>Certain products may be implemented within the user’s own infrastructure, in which case Artifit does not have access to the content of the data processed by the user through such solutions.</p>
          <p>For certain products, separate agreements may be concluded to regulate the processing of personal data in more detail.</p>
        </section>

        <section>
          <h2>5. Use of AI Technology</h2>
          <p>Certain Artifit products use artificial intelligence technologies for the purpose of searching, analyzing, organizing, and presenting information available to the user, including the processing of data from databases, documents, images, and other structured or unstructured sources connected to the system by the user.</p>
          <p>Depending on the user’s needs and the specific use case, particular solutions may use artificial intelligence models provided by third parties, as well as models executed within the user’s environment or dedicated infrastructure.</p>
          <p>The choice of model and infrastructure depends on the business, technical, and regulatory requirements of the particular user.</p>
          <p>Data made available to the system by the user are processed for the purpose of providing the functionalities requested by the user and performing the agreed services. For certain products and functionalities, the method and purposes of processing may be further regulated by separate contractual or accompanying documentation. Results generated through the use of AI functionalities are informational and advisory in nature. Artifit products do not independently make decisions that produce legal effects or similarly significant consequences for individuals. Final decisions are always made by the user.</p>
        </section>

        <section>
          <h2>6. Purposes and Legal Bases for Processing</h2>
          <p>Artifit processes personal data for the following purposes:</p>
          <ol className="pp-list-num">
            <li>
              Conclusion and Performance of Contracts:
              <ul>
                <li>provision of products and services;</li>
                <li>management of user accounts;</li>
                <li>provision of customer support;</li>
                <li>fulfillment of contractual obligations.</li>
              </ul>
            </li>
            <li>
              Legitimate Interest:
              <ul>
                <li>communication with prospective and existing clients;</li>
                <li>responding to inquiries and requests;</li>
                <li>ensuring system security;</li>
                <li>preventing misuse;</li>
                <li>resolving technical issues;</li>
                <li>improving the quality of products and services;</li>
                <li>protecting Artifit’s legal interests.</li>
              </ul>
            </li>
            <li>
              Compliance with Legal Obligations:
              <ul>
                <li>maintaining business records;</li>
                <li>fulfilling accounting and tax obligations;</li>
                <li>responding to requests from competent authorities.</li>
              </ul>
            </li>
            <li>Where required by applicable laws, processing will be carried out on the basis of prior consent.</li>
          </ol>
        </section>

        <section>
          <h2>7. Artifit’s Role in Data Processing</h2>
          <p>Depending on the circumstances, Artifit may act as either a data controller or a data processor.</p>
          <p>Artifit acts as a controller in relation to:</p>
          <ul>
            <li>data submitted via email;</li>
            <li>communication with prospective and existing clients;</li>
            <li>management of business relationships;</li>
            <li>contract administration;</li>
            <li>customer support;</li>
            <li>security and operation of the website and systems.</li>
          </ul>
          <p>When a user processes its own data or third-party data through our products, Artifit generally acts as a data processor and processes personal data exclusively in accordance with the documented instructions of the user, for the purpose of providing the contracted service and product functionalities.</p>
          <p>The parties’ rights and obligations regarding personal data processing may be regulated by a separate Data Processing Agreement (DPA) where applicable.</p>
          <p>Artifit may engage sub-processors for the provision and maintenance of services. An updated list of sub-processors is available upon request.</p>
        </section>

        <section>
          <h2>8. Business Communication</h2>
          <p>When you contact Artifit via email or other communication channels, we process the data contained in such communications for the purposes of:</p>
          <ul>
            <li>responding to inquiries;</li>
            <li>conducting negotiations;</li>
            <li>establishing business cooperation;</li>
            <li>concluding and performing contracts;</li>
            <li>managing business relationships.</li>
          </ul>
          <p>Data from business communications are retained throughout the duration of the communication and after its completion, to the extent and for the period necessary to protect Artifit’s legitimate business interests, establish or defend legal claims, and comply with legal obligations.</p>
        </section>

        <section>
          <h2>9. Recipients of Data</h2>
          <p>Data may be made available to:</p>
          <ul>
            <li>providers of cloud infrastructure and hosting services;</li>
            <li>providers of authentication and identity management services;</li>
            <li>providers of electronic communication services and business tools;</li>
            <li>providers of backup and security monitoring services;</li>
            <li>providers of artificial intelligence and machine learning services chosen by the user or included within the contracted solution;</li>
            <li>maintenance and technical support service providers;</li>
            <li>legal, tax, and other professional advisors;</li>
            <li>competent public authorities where required by law.</li>
          </ul>
          <p>Access to personal data is limited to persons who require such access in order to perform their duties.</p>
        </section>

        <section>
          <h2>10. International Data Transfers</h2>
          <p>Data are primarily processed using infrastructure located within the European Union.</p>
          <p>Depending on the configuration of a particular product, the selected AI model, integrations, and user requirements, certain processing activities may involve transfers of data outside the European Economic Area.</p>
          <p>Where such transfers occur, Artifit ensures the implementation of appropriate safeguards in accordance with applicable personal data protection laws.</p>
        </section>

        <section>
          <h2>11. Data Retention</h2>
          <p>Artifit retains data only for as long as necessary to achieve the purpose for which the data were collected or as required by applicable laws. In particular:</p>
          <ul>
            <li>user account data are retained for the duration of the contractual relationship and thereafter for as long as necessary to protect legitimate business interests or comply with legal obligations;</li>
            <li>data entered, created, or processed by users through the products, including AI functionalities, are retained in accordance with the agreement concluded with the user, the user’s instructions, and the service configuration;</li>
            <li>system and security logs are retained in accordance with the configuration of the services and infrastructure solutions used, typically for periods ranging from 30 days to 12 months.</li>
          </ul>
          <p>Backup copies of data are created to ensure service availability and reliability and are retained in accordance with applicable disaster recovery and business continuity policies.</p>
        </section>

        <section>
          <h2>12. Data Security</h2>
          <p>Artifit implements appropriate technical and organizational measures to protect data against unauthorized access, loss, misuse, or unauthorized disclosure.</p>
          <p>Security measures include, in particular:</p>
          <ul>
            <li>access control based on user authorizations;</li>
            <li>the principle of least privilege;</li>
            <li>protection of communications and data transmission;</li>
            <li>protection of systems against unauthorized access;</li>
            <li>logging and monitoring of security-relevant activities;</li>
            <li>backups and system recovery measures.</li>
          </ul>
          <p>Access to data is granted exclusively to persons who require such access for the performance of specific activities.</p>
        </section>

        <section>
          <h2>13. Your Rights</h2>
          <p>In accordance with applicable personal data protection laws, you have the right to:</p>
          <ul>
            <li>request access to your personal data;</li>
            <li>request correction of inaccurate or incomplete data;</li>
            <li>request deletion of data where legal conditions are met;</li>
            <li>request restriction of processing;</li>
            <li>object to processing;</li>
            <li>exercise your right to data portability where legal conditions are met;</li>
            <li>lodge a complaint with the Commissioner for Information of Public Importance and Personal Data Protection, at Bulevar kralja Aleksandra Street No. 15, 11120 Belgrade, telephone: +381 11 34 08 900, email: <a href="mailto:office@poverenik.rs">office@poverenik.rs</a>, or with another competent authority;</li>
            <li>seek judicial protection and compensation for damages in the event of unlawful processing.</li>
          </ul>
          <p>Requests may be submitted to: <a href="mailto:privacy@artifit.rs">privacy@artifit.rs</a>.</p>
        </section>

        <section>
          <h2>14. Changes to the Privacy Policy</h2>
          <p>Artifit reserves the right to amend this Privacy Policy from time to time in order to align it with changes in legislation, technological solutions, business processes, or our products and services.</p>
          <p>The current version of the Privacy Policy will be published on our website.</p>
        </section>
      </article>
    </main>
  );
}

export default PrivacyPolicy;
